import type {
  Measurement,
  AnnotationInGroup,
  SchemaAnnotation,
  EntryKey,
} from '@shared/types/measurements';

/**
 * Returns the list of cornerstone annotations regardless of legacy/new/mixed
 * shape.
 *
 * - New shape: `instrumentStateJson.annotations[]` — returned as-is.
 * - Legacy shape: `instrumentStateJson.annotation` (singular) — wrapped into a
 *   single-element array with a synthetic `_link`. The instrument code is
 *   taken from the matching schema annotation when available so the synthetic
 *   matches what `executeAppend` writes (the inventory `code`) rather than the
 *   cornerstone tool name — the two namespaces differ, and a mismatch makes the
 *   form schema's `validate()` reject the record with "no schema entry" errors.
 * - Mixed shape: both fields populated — happens when a record created via the
 *   legacy `createMeasurement` path is then appended to via `executeAppend`.
 *   The singular is promoted (synthetic `_link` with `index: 1`) and prepended
 *   to the array, deduplicated by `annotationUID` so the array wins when both
 *   reference the same UID.
 */
export const getAnnotations = (
  m: Pick<Measurement, 'instrumentStateJson' | 'measurementData'>
): AnnotationInGroup[] => {
  const isj = m.instrumentStateJson;
  const arr = isj?.annotations ?? [];
  const singular = isj?.annotation;

  if (!singular) {
    return arr;
  }

  // Prefer the schema annotation at index 1 (or the first one, if the record
  // only has one schema row). This keeps the synthetic `_link.instrument` in
  // the inventory-code namespace that `executeAppend` and the form schema
  // operate in, instead of the cornerstone-tool-name namespace.
  const schemaAnnotations = getSchemaAnnotations(m);
  const matchingSchema = schemaAnnotations.find(a => a.index === 1) ?? schemaAnnotations[0];
  const promoted = {
    ...singular,
    _link: {
      instrument: matchingSchema?.instrument ?? singular.metadata?.toolName ?? 'LEGACY',
      index: 1,
    },
  } as AnnotationInGroup;

  if (arr.length === 0) {
    return [promoted];
  }

  // Mixed shape: prepend the promoted singular unless the array already
  // contains an entry with the same UID (post-migration the array carries the
  // same cornerstone state as the singular).
  if (singular.annotationUID && arr.some(a => a.annotationUID === singular.annotationUID)) {
    return arr;
  }
  return [promoted, ...arr];
};

export const getSchemaAnnotations = (
  m: Pick<Measurement, 'measurementData'>
): SchemaAnnotation[] => {
  const data = m.measurementData?.data as { annotations?: SchemaAnnotation[] } | undefined;
  return data?.annotations ?? [];
};

export const findAnnotationByEntry = (
  m: Pick<Measurement, 'instrumentStateJson' | 'measurementData'>,
  entry: EntryKey
): AnnotationInGroup | undefined =>
  getAnnotations(m).find(
    a => a._link.instrument === entry.instrument && a._link.index === entry.index
  );

export const findSchemaAnnotationByEntry = (
  m: Pick<Measurement, 'measurementData'>,
  entry: EntryKey
): SchemaAnnotation | undefined =>
  getSchemaAnnotations(m).find(a => a.instrument === entry.instrument && a.index === entry.index);

/**
 * Next available 1-based entry index for the given instrument within a
 * measurement. Fills the smallest available gap so that deleting entry N and
 * adding again reuses N rather than leaving holes.
 */
export const nextEntryIndex = (
  m: Pick<Measurement, 'measurementData'>,
  instrument: string
): number => {
  const used = new Set(
    getSchemaAnnotations(m)
      .filter(a => a.instrument === instrument)
      .map(a => a.index)
  );
  let i = 1;
  while (used.has(i)) {
    i += 1;
  }
  return i;
};
